import { useEffect, useState, useCallback, useRef } from "react"

import useReigniteMetadata from "../lib/firebase/useReigniteMetadata"

import { useGetMasterEventMetadataQuery } from "../api/reigniteApi"
import { skipToken } from "@reduxjs/toolkit/query"

type UseFirebaseApiSource = (masterId: number | null, eventId: number | null) => {
  apiSource: 'reignite' | 'alaska' | null
  isFetchingFirebase: boolean
}

const useFirebaseApiSource: UseFirebaseApiSource = (masterId, eventId) => {
  const verifiedEventId = useRef<number | null>(null)

  const [selectedEventId, setSelectedEventId] = useState<number | null>(eventId)
  const [apiSource, setApiSource] = useState<'reignite' | 'alaska' | null>(null)

  const { data: masterReigniteData, isLoading: isMasterReigniteLoading } = useGetMasterEventMetadataQuery(masterId ?? skipToken, { skip: !!eventId })

  const { isFetchingFirebaseConfig, reigniteApiUsageConfig } = useReigniteMetadata()

  const checkIfMasterEventUseReignite = useCallback(() => {
    const areValuesExist = !!(masterId && reigniteApiUsageConfig)

    if (!areValuesExist || !(masterId in reigniteApiUsageConfig)) {
      return false
    }

    const master = reigniteApiUsageConfig[masterId]

    return master.blacklist.length === 0 && master.whitelist.length === 0
  }, [masterId, reigniteApiUsageConfig])

  const checkIfEventUseReignite = useCallback(() => {
    const areValuesExist = !!(selectedEventId && masterId && reigniteApiUsageConfig)

    if (!areValuesExist || !(masterId in reigniteApiUsageConfig)) {
      return false
    }

    const master = reigniteApiUsageConfig[masterId]

    return master.whitelist.includes(selectedEventId) && !master.blacklist.includes(selectedEventId)
  }, [selectedEventId, masterId, reigniteApiUsageConfig])

  useEffect(() => {
    if (eventId) {
      setSelectedEventId(eventId)
    } else if (!eventId && masterReigniteData) {
      setSelectedEventId(masterReigniteData.currentEvent?.id || masterReigniteData.prevEvent?.id || null)
    } else {
      setSelectedEventId(null)
    }
  }, [eventId, masterReigniteData])

  useEffect(() => {
    setApiSource(null)

    const isMasterMetadataMissingButNeeded = !eventId && !masterReigniteData
    if (!masterId || !reigniteApiUsageConfig || !selectedEventId || isMasterMetadataMissingButNeeded) {
      return
    }

    verifiedEventId.current = selectedEventId

    if (checkIfMasterEventUseReignite() || checkIfEventUseReignite()) {
      setApiSource('reignite')
      return
    }

    setApiSource('alaska')
  }, [masterId, selectedEventId, reigniteApiUsageConfig, checkIfMasterEventUseReignite, checkIfEventUseReignite, masterReigniteData, eventId])

  return {
    apiSource: verifiedEventId.current === selectedEventId ? apiSource : null,
    isFetchingFirebase: isFetchingFirebaseConfig || isMasterReigniteLoading
  }
}

export { useFirebaseApiSource }